import React, { useEffect, useMemo, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";

import Navigation from "../Components/Navigation";
import API from "../api/config.js";
import logo from "../assets/TravelXOXO 1.png"

const APP_ORIGIN = process.env.REACT_APP_ORIGIN || window.location.origin;


const EyeIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor">
    <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Z" strokeWidth="2" />
    <circle cx="12" cy="12" r="3" strokeWidth="2" />
  </svg>
);


// ---- status pill ----
const StatusPill = ({ status }) => {
  const map =
    status === "Active"
      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
      : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300";
  return <span className={`px-3 py-1 rounded-full text-xs font-semibold ${map}`}>{status}</span>;
};

const ActionBtn = ({ title, onClick, children }) => (
  <button
    type="button"
    title={title}
    onClick={onClick}
    className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
  >
    {children}
  </button>
);

// ---- filter + sort options ----
const statusOptions = ["All Status", "Active", "Inactive"];
const sortOptions = [
  { value: "scans_desc", label: "Most Scanned" },
  { value: "scans_asc",  label: "Least Scanned" },
  { value: "item_asc",   label: "Item (A → Z)" },
  { value: "owner_asc",  label: "Owner (A → Z)" },
  { value: "last_desc",  label: "Recently Scanned" },
  { value: "last_asc",   label: "Oldest Scanned" },
];

const QRCodes = () => {
  const [theme, setTheme] = useState(() => localStorage.getItem("travelxoxo-theme") || "light");
  const [activeScreen, setActiveScreen] = useState("qrcodes");
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);

  
  // UI controls (same structure as Users)
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("All Status");
  const [sortBy, setSortBy] = useState("scans_desc");
  // Pagination state
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 100;



  // ✅ ADD THIS BLOCK HERE
  const [previewQR, setPreviewQR] = useState(null);

  function handleView(row) { setPreviewQR(row); }
  function closePreview() { setPreviewQR(null); }

  function handleDownloadFromPreview() {
    const canvas = document.getElementById("qr-preview");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `travelxoxo-qr-${previewQR.qr_id}.png`;
    a.click();
  }
  // ✅ END BLOCK

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("travelxoxo-theme", theme);
  }, [theme]);
  const toggleTheme = () => setTheme((t) => (t === "light" ? "dark" : "light"));

  const fetchAllCodes = async () => {
    try {
      setLoading(true);
      const res = await API.get("/qrcodes");
      const data = (res.data || []).map((c) => ({
        qr_id: c.qr_id,
        project_name: c.project_name || "",
        status: c.status?.toUpperCase() === "ACTIVE" ? "Active" : "Inactive",
        item: c.item || "",
        owner: c.owner || "",
        scan_count: Number(c.scan_count ?? 0),
        last_scanned: c.last_scanned || null,
      }));
      setCodes(data);
    } catch (err) {
      console.error("Error fetching QR codes:", err);
      alert(`Error fetching QR codes: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllCodes();
  }, []);

  // --- Filtering + Sorting ---
  const filtered = useMemo(() => {
    let list = [...codes];
    const q = query.trim().toLowerCase();

    if (q) {
      list = list.filter(
        (r) =>
          r.qr_id?.toLowerCase().includes(q) ||
          r.item?.toLowerCase().includes(q) ||
          r.owner?.toLowerCase().includes(q)
      );
    }

    if (status !== "All Status") {
      list = list.filter((r) => r.status === status);
    }

    list.sort((a, b) => {
      switch (sortBy) {
        case "scans_asc":  return (a.scan_count ?? 0) - (b.scan_count ?? 0);
        case "scans_desc": return (b.scan_count ?? 0) - (a.scan_count ?? 0);
        case "item_asc":   return (a.item || "").localeCompare(b.item || "");
        case "owner_asc":  return (a.owner || "").localeCompare(b.owner || "");
        case "last_asc":   return new Date(a.last_scanned || 0) - new Date(b.last_scanned || 0);
        case "last_desc":  return new Date(b.last_scanned || 0) - new Date(a.last_scanned || 0);
        default: return 0;
      }
    });
    return list;
  }, [codes, query, status, sortBy]);

  // Reset to page 1 whenever filters or search changes
useEffect(() => {
  setPage(1);
}, [query, status, sortBy]);


  // Pagination calculations
const totalPages = Math.ceil(filtered.length / PAGE_SIZE) || 1;
const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);


  const formatAgo = (iso) => {
    if (!iso) return "—";
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} hour${hrs > 1 ? "s" : ""} ago`;
    const days = Math.floor(hrs / 24);
    return `${days} day${days > 1 ? "s" : ""} ago`;
  };

  

  return (
    <div className="flex bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors duration-300">
      <Navigation
        activeScreen={activeScreen}
        setActiveScreen={setActiveScreen}
        theme={theme}
        toggleTheme={toggleTheme}
      />

      <main className="flex-1 ml-20 md:ml-64 p-6 md:p-10">
        <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">QR Code Management</h1>

         
        </div>

        {/* ✅ Controls (from Users.jsx) */}
        <div className="grid gap-3 md:grid-cols-3 mb-5">
          <div className="col-span-1">
            <div className="relative">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search QR codes..."
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-800 placeholder-gray-400 focus:border-gray-400 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              />
              <span className="pointer-events-none absolute right-3 top-2.5 text-gray-400">⌘K</span>
            </div>
          </div>

          <div>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full appearance-none rounded-xl border border-gray-200 bg-white px-4 py-2.5 pr-10 text-gray-800 focus:border-gray-400 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
            >
              {statusOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full appearance-none rounded-xl border border-gray-200 bg-white px-4 py-2.5 pr-10 text-gray-800 focus:border-gray-400 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
            >
              {sortOptions.map((so) => (
                <option key={so.value} value={so.value}>
                  {so.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-gray-50 text-gray-600 dark:bg-gray-900/40 dark:text-gray-300">
                <tr>
                  <th className="px-6 py-4 font-semibold">QR Code</th>
                  <th className="px-6 py-4 font-semibold">Project Name </th>
                  <th className="px-6 py-4 font-semibold">Item</th>
                  <th className="px-6 py-4 font-semibold">Owner</th>
             
                  <th className="px-6 py-4 font-semibold">Scan Count</th>
                  <th className="px-6 py-4 font-semibold">Last Scanned</th>
                  <th className="px-6 py-4 font-semibold">Status</th>
                  <th className="px-6 py-4 font-semibold text-right">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {loading && (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                      Loading…
                    </td>
                  </tr>
                )}

                {!loading &&
                  paginated.map((r) => (
                    <tr key={r.qr_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-6 py-4 font-mono text-sm text-gray-900 dark:text-gray-100">
                        {r.qr_id}
                      </td>
                      <td className="px-6 py-4 text-gray-800 dark:text-gray-200">{r.project_name || "—"}</td>
                      <td className="px-6 py-4 text-gray-800 dark:text-gray-200">{r.item || "—"}</td>
                      <td className="px-6 py-4 text-gray-800 dark:text-gray-200">{r.owner || "—"}</td>
                      
                
                      <td className="px-6 py-4 font-semibold text-gray-900 dark:text-gray-100">
                        {r.scan_count ?? 0}
                      </td>
                      <td className="px-6 py-4 text-gray-700 dark:text-gray-300">
                        {formatAgo(r.last_scanned)}
                      </td>
                      <td className="px-6 py-4">
                        <StatusPill status={r.status} />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <ActionBtn title="View" onClick={() => handleView(r)}>
                            <EyeIcon />
                          </ActionBtn>
                          
                         
                        </div>
                      </td>
                    </tr>
                  ))}

                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                      No QR codes found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          
        </div>




{previewQR && (
          
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
  <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-800">
    <div className="mb-4 text-sm text-gray-600 dark:text-gray-300">
      QR for <span className="font-mono">{previewQR.qr_id}</span>
    </div>

    <div className="flex flex-col items-center">
      <div className="relative inline-flex flex-col items-center rounded-2xl border-4 border-orange-500 bg-white p-3">
        <QRCodeCanvas
          id="qr-preview"
          value={`${APP_ORIGIN}/${previewQR.qr_id}`}
          size={240}
          level="H"
          fgColor="#EF7F1A"
          bgColor="#ffffff"
          includeMargin={false}
        />
        <img src={logo} alt="Travelxoxo Logo" className="mt-2 h-6 w-auto" />
      </div>

      {/* ✅ Scanner URL below QR */}
      <a
        href={`${APP_ORIGIN}/${previewQR.qr_id}`}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 text-blue-600 underline break-all dark:text-blue-400 text-sm text-center"
      >
        {`${APP_ORIGIN}/${previewQR.qr_id}`}
      </a>

      <div className="mt-6 flex gap-2">
        <button
          onClick={handleDownloadFromPreview}
          className="rounded-xl bg-gray-900 px-4 py-2 text-white dark:bg-gray-100 dark:text-gray-900"
        >
          Download PNG
        </button>
        <button
          onClick={closePreview}
          className="rounded-xl bg-gray-200 px-4 py-2 dark:bg-gray-700 dark:text-gray-100"
        >
          Close
        </button>
      </div>
    </div>
  </div>
</div>

        )}

   {/* Pagination */}
<div className="flex items-center justify-center gap-2 px-4 py-5">
  
  {/* Prev */}
  <button
    className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm 
               text-gray-700 hover:bg-gray-100 disabled:opacity-40 
               dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
    onClick={() => setPage((p) => Math.max(1, p - 1))}
    disabled={page === 1}
  >
    ←
  </button>

  {/* Page numbers (max 5 visible) */}
  {Array.from({ length: totalPages }).slice(
    Math.max(0, page - 3),
    Math.min(totalPages, page + 2)
  ).map((_, i) => {
    const n = i + Math.max(1, page - 2);
    const active = n === page;
    return (
      <button
        key={n}
        onClick={() => setPage(n)}
        className={`h-10 w-10 rounded-xl text-sm font-semibold transition ${
          active
            ? "bg-orange-600 text-white shadow"
            : "border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
        }`}
      >
        {n}
      </button>
    );
  })}

  {/* Next */}
  <button
    className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm 
               text-gray-700 hover:bg-gray-100 disabled:opacity-40 
               dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
    disabled={page === totalPages}
  >
    →
  </button>
</div>
      </main>



   

    </div>
);

};

export default QRCodes;
